import { Loading } from "@/component/Loading";
import { AspectRatio } from "@/types/common";
import classNames from "classnames";
import React, { createContext, useContext } from "react";

export const PreviewAspectContext = createContext<{
  isHeightBound: boolean;
}>({
  isHeightBound: false,
});

export const usePreviewAspect = () => useContext(PreviewAspectContext);

/**
 * A container that enforces the 16:9 or 9:16 aspect ratio.
 */
export const PreviewAspectContainer = ({
  aspectRatio,
  children,
  isLoading,
  className,
  showBackground = true,
}: {
  aspectRatio: AspectRatio;
  children: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  showBackground?: boolean;
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [size, setSize] = React.useState<{
    width: number;
    height: number;
    isHeightBound: boolean;
  } | null>(null);

  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const updateSize = () => {
      const { clientWidth: pw, clientHeight: ph } = wrapper;
      if (pw === 0 || ph === 0) return;

      const targetRatio =
        aspectRatio === AspectRatio.LANDSCAPE ? 16 / 9 : 9 / 16;
      const parentRatio = pw / ph;

      let width, height, isHeightBound;
      if (parentRatio > targetRatio) {
        // Height is the constraint
        height = ph;
        width = ph * targetRatio;
        isHeightBound = true;
      } else {
        // Width is the constraint
        width = pw;
        height = pw / targetRatio;
        isHeightBound = false;
      }
      setSize({ width, height, isHeightBound });
    };

    const observer = new ResizeObserver(updateSize);
    observer.observe(wrapper);
    updateSize();

    return () => observer.disconnect();
  }, [aspectRatio]);

  return (
    <div
      ref={wrapperRef}
      className="w-full h-full flex items-center justify-center overflow-hidden"
    >
      <div
        className={classNames(
          "relative overflow-hidden flex items-center justify-center shrink-0",
          showBackground && "bg-black",
          className
        )}
        style={
          size
            ? { width: size.width, height: size.height }
            : { width: "100%", height: "100%", opacity: 0 }
        }
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <PreviewAspectContext.Provider
            value={{ isHeightBound: size?.isHeightBound ?? false }}
          >
            {children}
          </PreviewAspectContext.Provider>
        </div>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <Loading fontSize={24} color="white" />
          </div>
        )}
      </div>
    </div>
  );
};
